import { weekdaysOnOffZodObject } from "@@/shared/acquire/accommodation-engine/weekdays-on-off";
import {
    accommodationRangeIdFactory,
    accommodationRangeIdZodSchema,
} from "@@/shared/acquire/accommodation-range-id";
import { accommodationSettingsZodSchema } from "@@/shared/acquire/bookable-settings";
import { hourMinuteZodObject } from "@@/shared/date-time/hour-minute";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { translatableZodSchema } from "@@/shared/translations/translation";
import type { Except } from "type-fest";
import { z } from "zod";

// Availability Settings
const accommodationAvailabilitySettingsZodObject = z.object({
    minimumStay: z.number(),
    maximumStay: z.number(),
    checkInTime: hourMinuteZodObject,
    checkOutTime: hourMinuteZodObject,

    checkIn: weekdaysOnOffZodObject.optional(),
    checkOut: weekdaysOnOffZodObject.optional(),

    settings: accommodationSettingsZodSchema,
});
type AccommodationAvailabilitySettings = z.infer<
    typeof accommodationAvailabilitySettingsZodObject
>;

// Availability Range
const accommodationAvailabilityRangeZodObject = z.object({
    _id: accommodationRangeIdZodSchema,
    _type: z.literal("ACCOMMODATION_AVAILABILITY_RANGE"),
    _createdAt: isoAndUnixZodSchema,
    title: translatableZodSchema,
    start: isoAndUnixZodSchema,
    end: isoAndUnixZodSchema,

    settings: accommodationAvailabilitySettingsZodObject,
});
type AccommodationAvailabilityRange = z.infer<
    typeof accommodationAvailabilityRangeZodObject
>;
const accommodationAvailabilityRangeFactoryParamsZodObject =
    accommodationAvailabilityRangeZodObject.omit({
        _id: true,
        _type: true,
    });

type AvailabilityRangeFactoryParams = Except<
    AccommodationAvailabilityRange,
    "_id" | "_type" | "_createdAt"
>;
const accommodationAvailabilityRangeFactory = (
    params: AvailabilityRangeFactoryParams,
) => {
    const command: AccommodationAvailabilityRange = {
        ...params,
        _id: accommodationRangeIdFactory(),
        _type: "ACCOMMODATION_AVAILABILITY_RANGE",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

// Availability
const accommodationAvailabilityZodObject = z.object({
    availabilityRanges: z.array(accommodationAvailabilityRangeZodObject),
    baseSettings: accommodationAvailabilitySettingsZodObject,
});
type AccommodationAvailability = z.infer<
    typeof accommodationAvailabilityZodObject
>;

export {
    accommodationAvailabilityRangeFactory,
    accommodationAvailabilityRangeFactoryParamsZodObject,
    accommodationAvailabilityRangeZodObject,
    accommodationAvailabilitySettingsZodObject,
    accommodationAvailabilityZodObject,
};
export type {
    AccommodationAvailability,
    AccommodationAvailabilityRange,
    AccommodationAvailabilitySettings,
};
